import { getAuth } from "firebase/auth";
import React, { useState, useEffect, useContext } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
  query,
  orderBy,
} from "firebase/firestore";
import { firestore } from "./firebase-config";
import { AuthContext } from "./AuthContext";

const CompendiumBuilder = () => {
  const { loggedInUser } = useContext(AuthContext);
  const [topics, setTopics] = useState([]);
  const [newTopic, setNewTopic] = useState("");
  const [topicContent, setTopicContent] = useState("");
  const [subtopics, setSubtopics] = useState({});
  const [newSubtopic, setNewSubtopic] = useState("");
  const [subtopicContent, setSubtopicContent] = useState("");
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [editingTopic, setEditingTopic] = useState(null);
  const [editingSubtopic, setEditingSubtopic] = useState(null);
  const [editTopicName, setEditTopicName] = useState("");
  const [editTopicContent, setEditTopicContent] = useState("");
  const [editSubtopicName, setEditSubtopicName] = useState("");
  const [editSubtopicContent, setEditSubtopicContent] = useState("");
  const [isEditMode, setIsEditMode] = useState(true);

  useEffect(() => {
    if (loggedInUser) {
      fetchTopics();
    }
  }, [loggedInUser]);

  const fetchTopics = async () => {
    try {
      const topicsRef = collection(firestore, "users", loggedInUser.uid, "topics");
      const topicsSnapshot = await getDocs(query(topicsRef, orderBy("name")));
      const fetchedTopics = topicsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setTopics(fetchedTopics);
    } catch (error) {
      console.error("Error fetching topics:", error);
    }
  };

  const fetchSubtopics = async (topicId) => {
    try {
      const subtopicsRef = collection(firestore, "users", loggedInUser.uid, "topics", topicId, "subtopics");
      const subtopicsSnapshot = await getDocs(query(subtopicsRef, orderBy("name")));
      const fetchedSubtopics = subtopicsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setSubtopics((prev) => ({ ...prev, [topicId]: fetchedSubtopics }));
    } catch (error) {
      console.error("Error fetching subtopics:", error);
    }
  };

  const addTopic = async () => {
    if (!newTopic.trim()) return;
    try {
      const topicsRef = collection(firestore, "users", loggedInUser.uid, "topics");
      await addDoc(topicsRef, { name: newTopic, content: topicContent });
      setNewTopic("");
      setTopicContent("");
      fetchTopics();
    } catch (error) {
      console.error("Error adding topic:", error);
    }
  };

  const addSubtopic = async (topicId) => {
    if (!newSubtopic.trim()) return;
    try {
      const subtopicsRef = collection(firestore, "users", loggedInUser.uid, "topics", topicId, "subtopics");
      await addDoc(subtopicsRef, { name: newSubtopic, content: subtopicContent });
      setNewSubtopic("");
      setSubtopicContent("");
      fetchSubtopics(topicId);
    } catch (error) {
      console.error("Error adding subtopic:", error);
    }
  };

  const updateTopic = async (topicId) => {
    if (!editTopicName.trim()) return;
    try {
      const topicRef = doc(firestore, "users", loggedInUser.uid, "topics", topicId);
      await updateDoc(topicRef, { name: editTopicName, content: editTopicContent });
      setEditingTopic(null);
      setEditTopicName("");
      setEditTopicContent("");
      fetchTopics();
    } catch (error) {
      console.error("Error updating topic:", error);
    }
  };

  const updateSubtopic = async (topicId, subtopicId) => {
    if (!editSubtopicName.trim()) return;
    try {
      const subtopicRef = doc(firestore, "users", loggedInUser.uid, "topics", topicId, "subtopics", subtopicId);
      await updateDoc(subtopicRef, { name: editSubtopicName, content: editSubtopicContent });
      setEditingSubtopic(null);
      setEditSubtopicName("");
      setEditSubtopicContent("");
      fetchSubtopics(topicId);
    } catch (error) {
      console.error("Error updating subtopic:", error);
    }
  };

  const deleteTopic = async (topicId) => {
    try {
      await deleteDoc(doc(firestore, "users", loggedInUser.uid, "topics", topicId));
      setSelectedTopicId(null);
      fetchTopics();
    } catch (error) {
      console.error("Error deleting topic:", error);
    }
  };

  const deleteSubtopic = async (topicId, subtopicId) => {
    try {
      await deleteDoc(doc(firestore, "users", loggedInUser.uid, "topics", topicId, "subtopics", subtopicId));
      fetchSubtopics(topicId);
    } catch (error) {
      console.error("Error deleting subtopic:", error);
    }
  };

  const startEditTopic = (topic) => {
    setEditingTopic(topic.id);
    setEditTopicName(topic.name);
    setEditTopicContent(topic.content || "");
  };

  const startEditSubtopic = (subtopic) => {
    setEditingSubtopic(subtopic.id);
    setEditSubtopicName(subtopic.name);
    setEditSubtopicContent(subtopic.content || "");
  };

  const cancelEdit = () => {
    setEditingTopic(null);
    setEditingSubtopic(null);
    setEditTopicName("");
    setEditTopicContent("");
    setEditSubtopicName("");
    setEditSubtopicContent("");
  };

  const handleTopicClick = (topicId) => {
    setSelectedTopicId(topicId);
    if (!subtopics[topicId]) {
      fetchSubtopics(topicId);
    }
  };

  const toggleEditMode = () => {
    setIsEditMode((prev) => !prev);
    cancelEdit(); // Cancel any active edits when toggling modes
  };

  const renderLinkedContent = (html) => {
    return html.replace(/\b([A-Za-z ]{3,})\b/g, (match) => {
      const topic = topics.find((t) => t.name.toLowerCase() === match.toLowerCase());
      return topic ? `<a href="#${topic.id}" style="color: #4FC3F7; text-decoration: underline;">${match}</a>` : match;
    });
  };

  useEffect(() => {
    const handleHashChange = () => {
      const id = window.location.hash.substring(1);
      const element = document.getElementById(id);
      if (element) {
        setSelectedTopicId(id);
        element.scrollIntoView({ behavior: "smooth" });
        if (!subtopics[id]) {
          fetchSubtopics(id);
        }
      }
    };
    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [topics]);

  if (!loggedInUser) return <div style={{ padding: 20, color: "red" }}>You must be logged in to access the compendium.</div>;

  return (
    <div style={{ maxWidth: 800, margin: "auto", padding: 20, backgroundColor: "#222", borderRadius: 8, color: "#fff", textAlign: "left" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ textAlign: "left", flex: 1, margin: 0 }}>Compendium Builder</h2>
        <button
          onClick={toggleEditMode}
          style={{
            padding: 10,
            backgroundColor: isEditMode ? "#FFA500" : "#2196F3",
            color: "white",
            border: "none",
            borderRadius: 4,
            cursor: "pointer"
          }}
        >
          {isEditMode ? "Switch to View Mode" : "Switch to Edit Mode"}
        </button>
      </div>

      {isEditMode && (
        <>
          <input
            type="text"
            value={newTopic}
            onChange={(e) => setNewTopic(e.target.value)}
            placeholder="New Topic Title"
            style={{ width: "92%", padding: 10, marginBottom: 10, borderRadius: 4, border: "1px solid #ccc" }}
          />
          <ReactQuill
            theme="snow"
            value={topicContent}
            onChange={setTopicContent}
            placeholder="Topic details..."
            style={{width: "100%", marginBottom: 20, backgroundColor: "#1a1a1a", color: "white" , borderRadius: 4, border: "1px solid #ccc"}}
          />
          <button
            onClick={addTopic}
            style={{ width: "100%", padding: 10, cursor: "pointer", marginBottom: 20, backgroundColor: "#4CAF50", color: "white", border: "none", borderRadius: 4 }}
          >
            Add Topic
          </button>
        </>
      )}

      {topics.map((topic) => (
        <div key={topic.id} id={topic.id} style={{ marginBottom: 20 }}>
          {isEditMode && editingTopic === topic.id ? (
            <div style={{ backgroundColor: "#333", padding: 10, borderRadius: 4 }}>
              <input
                type="text"
                value={editTopicName}
                onChange={(e) => setEditTopicName(e.target.value)}
                placeholder="Edit Topic Title"
                style={{ width: "90%", padding: 8, marginBottom: 8, borderRadius: 4, border: "1px solid #ccc" }}
              />
              <ReactQuill
                theme="snow"
                value={editTopicContent}
                onChange={setEditTopicContent}
                placeholder="Edit Topic details..."
                style={{ marginBottom: 10, backgroundColor: "1a1a1a", color: "white", borderRadius: 4, border: "1px solid #ccc" }}
              />
              <button
                onClick={() => updateTopic(topic.id)}
                style={{ padding: 8, marginRight: 5, backgroundColor: "#2196F3", color: "white", border: "none", borderRadius: 4 }}
              >
                Save
              </button>
              <button
                onClick={cancelEdit}
                style={{ padding: 8, backgroundColor: "#f44336", color: "white", border: "none", borderRadius: 4 }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <a href={`#${topic.id}`} style={{ textDecoration: "none", color: "inherit" }}>
            <div
              onClick={() => handleTopicClick(topic.id)}
              style={{ backgroundColor: "#333", padding: 10, borderRadius: 4, marginBottom: 10, textAlign: "left" }}
            >
                
              <strong>{topic.name}</strong>
              
              {isEditMode && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); startEditTopic(topic); }}
                    style={{ float: "right", marginLeft: 5, background: "#2196F3", color: "white", border: "none", borderRadius: 4 }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteTopic(topic.id); }}
                    style={{ float: "right", background: "red", color: "white", border: "none", borderRadius: 4 }}
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
            </a>
          )}

          {selectedTopicId === topic.id && (
            <div style={{ paddingLeft: 0, marginTop: 10 }}>
              <div
                dangerouslySetInnerHTML={{ __html: renderLinkedContent(topic.content || "") }}
                style={{ backgroundColor: "#1a1a1a", padding: 10, borderRadius: 4, marginBottom: 10, textAlign: "left" }}
              />

              {isEditMode && (
                <>
                  <input
                    type="text"
                    value={newSubtopic}
                    onChange={(e) => setNewSubtopic(e.target.value)}
                    placeholder="New Subtopic Title"
                    style={{ width: "92%", padding: 8, marginBottom: 8, borderRadius: 4, border: "1px solid #ccc" }}
                  />
                  <ReactQuill
                    theme="snow"
                    value={subtopicContent}
                    onChange={setSubtopicContent}
                    placeholder="Subtopic details..."
                    style={{ marginBottom: 10, backgroundColor: "1a1a1a", color: "white" , borderRadius: 4, border: "1px solid #ccc"}}
                  />
                  <button
                    onClick={() => addSubtopic(topic.id)}
                    style={{ width: "100%", padding: 8, marginBottom: 10, cursor: "pointer", backgroundColor: "#FFA500", color: "white", border: "none", borderRadius: 4 }}
                  >
                    Add Subtopic
                  </button>
                </>
              )}

              <ul style={{ listStyleType: "none", paddingLeft: 0 }}>
                {(subtopics[topic.id] || []).map((sub) => (
                  <li key={sub.id} id={sub.id} style={{ padding: "10px 0", borderBottom: "1px solid #444", textAlign: "left" }}>
                    {isEditMode && editingSubtopic === sub.id ? (
                      <div>
                        <input
                          type="text"
                          value={editSubtopicName}
                          onChange={(e) => setEditSubtopicName(e.target.value)}
                          placeholder="Edit Subtopic Title"
                          style={{ width: "92%", padding: 8, marginBottom: 8, borderRadius: 4, border: "1px solid #ccc" }}
                        />
                        <ReactQuill
                          theme="snow"
                          value={editSubtopicContent}
                          onChange={setEditSubtopicContent}
                          placeholder="Edit Subtopic details..."
                          style={{ marginBottom: 10, backgroundColor: "1a1a1a", color: "white", borderRadius: 4, border: "1px solid #ccc" }}
                        />
                        <button
                          onClick={() => updateSubtopic(topic.id, sub.id)}
                          style={{ padding: 8, marginRight: 5, backgroundColor: "#2196F3", color: "white", border: "none", borderRadius: 4 }}
                        >
                          Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          style={{ padding: 8, backgroundColor: "#f44336", color: "white", border: "none", borderRadius: 4 }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <strong>{sub.name}</strong>
                        {isEditMode && (
                          <>
                            <button
                              onClick={() => startEditSubtopic(sub)}
                              style={{ marginLeft: 10, background: "#2196F3", color: "white", border: "none", borderRadius: 4 }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => deleteSubtopic(topic.id, sub.id)}
                              style={{ marginLeft: 5, background: "red", color: "white", border: "none", borderRadius: 4 }}
                            >
                              Delete
                            </button>
                          </>
                        )}
                        <div
                          dangerouslySetInnerHTML={{ __html: renderLinkedContent(sub.content || "") }}
                          style={{ marginTop: 5, padding: 5, backgroundColor: "#1a1a1a", borderRadius: 4, textAlign: "left" }}
                        />
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default CompendiumBuilder;